import { Trans } from '@lingui/react/macro';

import ArticlePhoto from '@/components/ArticlePhoto';
import Stack from '@/design_system/Stack';
import Paragraph from '@/design_system/Typography/Paragraph';
import { getArticlePhoto, useArticleName } from '@/models/article';
import { getScopedBoolean, useWorkflowPublic } from '@/models/workflow';
import { createBEMClasses } from '@/utils/classname';
import { formatWeight } from '@/utils/number';
import { useRequestName } from '@/utils/useRequestName';

import { ShipmentFormArticle } from '../../ArticleSelect';

import './ArticleNameCell.css';

const { block } = createBEMClasses('article-name-cell');

export const ArticleNameCell = ({
  article,
  photoSize,
  badge,
}: {
  article: ShipmentFormArticle;
  photoSize: 'small' | 'medium' | 'large';
  badge?: React.ReactNode;
}) => {
  const articleName = useArticleName({ article });

  return (
    <Stack className={block()} ariaLabel={articleName}>
      <ArticlePhoto photo={getArticlePhoto(article)} size={photoSize} />
      <div style={{ overflow: 'hidden' }}>
        <Stack row alignItems="center" style={{ columnGap: '0.5rem', rowGap: 0 }}>
          <Paragraph weight="medium" ellipsis>
            {articleName}
          </Paragraph>
          {badge}
        </Stack>

        {article.product?.data.reference && (
          <Paragraph size="200" color="secondary">
            <Trans id="article.reference.short">Ref:</Trans> {article.product.data.reference}
            {!!article.weight?.amount && ` • ${formatWeight(article.weight)}`}
          </Paragraph>
        )}

        {article.request && <RequestDetails article={article} />}
      </div>
    </Stack>
  );
};

const RequestDetails = ({ article }: { article: ShipmentFormArticle }) => {
  const request = article.request!;

  const { data: workflow } = useWorkflowPublic(request.workflowId);

  const isClientEnabled = getScopedBoolean({
    scopedBoolean: workflow?.config.fields?.request?.client?.enabled,
    credentialsScopes: article.scopes,
  });

  const requestName = useRequestName({
    request: { ...request, organization: article.organization },
    isClientEnabled,
  });

  return (
    <Paragraph size="200" color="secondary">
      [{request.reference}] {requestName.major} {!!requestName.minor && `(${requestName.minor})`}
    </Paragraph>
  );
};
